import { NgClass } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    OnDestroy,
    ViewChild,
    computed,
    input,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { TrustPipe } from '@frontend/sports/common/core/utils/dom';
import {
    CustomLoadingIndicatorService,
    ILoadingIndicatorHandler,
    LOADING_INDICATOR,
    LoadingIndicatorModule,
} from '@frontend/sports/loading-indicator-feature';
import { DeviceService } from '@frontend/vanilla/core';
import { distinctUntilChanged } from 'rxjs';

import { NavigationPayload, StoryNavigation } from '../story-content.model';
import { StoryContentService } from '../story-content.service';

@Component({
    selector: 'ms-story-content-video',
    templateUrl: './story-content-video.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [{ provide: LOADING_INDICATOR, useValue: 'STORYMODULE_VIDEO' }],
    imports: [NgClass, LoadingIndicatorModule, TrustPipe],
})
export class StoryContentVideoComponent implements AfterViewInit, OnDestroy {
    videoUrl = input.required<string>();
    subStoryId = input.required<string>();
    storyId = input.required<string>();
    @ViewChild('video') videoRef: ElementRef<HTMLVideoElement>;
    private loadingIndicator: ILoadingIndicatorHandler | null;
    muteAudio = true;
    muteVisible = false;
    videoReady = false;

    get mobileLandscape(): boolean {
        return this.deviceService.isMobilePhone && this.deviceService.currentOrientation === 'landscape';
    }

    constructor(
        private storyContentService: StoryContentService,
        private loadingIndicatorService: CustomLoadingIndicatorService,
        private deviceService: DeviceService,
        private changeDetectorRef: ChangeDetectorRef,
    ) {
        this.storyContentService.statusChange.pipe(distinctUntilChanged(), takeUntilDestroyed()).subscribe((paused) => {
            if (!paused && (this.mobileLandscape || !this.videoReady)) {
                this.storyContentService.pause();
            } else {
                if (paused) {
                    this.pause();
                } else {
                    this.play();
                }
                this.changeDetectorRef.markForCheck();
            }
        });
    }

    ngAfterViewInit(): void {
        this.video.onloadstart = () => {
            this.videoReady = false;
            this.showLoader();
            this.pause(true);
        };

        this.video.oncanplay = () => {
            this.videoReady = true;
            this.muteVisible = true;
            this.hideLoader();
            this.play(true);
        };
        this.video.onerror = this.video.onended = () => this.handleVideoEvent();
    }

    private handleVideoEvent(): void {
        const navigationPayload: NavigationPayload = {
            action: StoryNavigation.NextSubStoryWithoutTracking,
            payload: computed(() => {
                return {
                    storyId: this.storyId(),
                    subStoryId: this.subStoryId(),
                };
            })(),
        };
        this.storyContentService.navigate(navigationPayload);
    }

    ngOnDestroy(): void {
        this.destroyVideoElement();
    }

    get video(): HTMLVideoElement {
        return this.videoRef.nativeElement;
    }

    toggleMute(): void {
        this.muteAudio = !this.muteAudio;
    }

    private play(updateDuration: boolean = false): void {
        this.video.play();

        if (updateDuration) {
            this.storyContentService.play();
        }
    }

    private pause(updateDuration: boolean = false): void {
        this.video.pause();

        if (updateDuration) {
            this.storyContentService.pause();
        }
    }

    private showLoader(): void {
        this.loadingIndicator = this.loadingIndicatorService.start('STORYMODULE_VIDEO');
    }

    private hideLoader(): void {
        this.loadingIndicator?.done();
    }

    private destroyVideoElement(): void {
        this.video.pause();
        this.video.removeAttribute('src');
        this.video.load();
    }
}
